import { Style } from '@boba-cli/chapstick';
import { newBinding, matches } from '@boba-cli/key';
import { MouseMsg, KeyMsg, MouseAction, MouseButton } from '@boba-cli/tea';

// src/model.ts
var defaultKeyMap = {
  pageDown: newBinding({ keys: ["pgdown", " ", "f"] }),
  pageUp: newBinding({ keys: ["pgup", "b"] }),
  halfPageDown: newBinding({ keys: ["d", "ctrl+d"] }),
  halfPageUp: newBinding({ keys: ["u", "ctrl+u"] }),
  down: newBinding({ keys: ["down", "j"] }),
  up: newBinding({ keys: ["up", "k"] })
};

// src/messages.ts
var SyncMsg = class {
  constructor(lines, topLine, bottomLine) {
    this.lines = lines;
    this.topLine = topLine;
    this.bottomLine = bottomLine;
  }
  _tag = "viewport-sync";
};
var ScrollMsg = class {
  constructor(percent, topLine) {
    this.percent = percent;
    this.topLine = topLine;
  }
  _tag = "viewport-scroll";
};

// src/model.ts
var ViewportModel = class _ViewportModel {
  width;
  height;
  keyMap;
  mouseWheelEnabled;
  mouseWheelDelta;
  yOffset;
  lines;
  style;
  constructor(options) {
    this.width = Math.max(0, options.width);
    this.height = Math.max(0, options.height);
    this.keyMap = options.keyMap;
    this.mouseWheelEnabled = options.mouseWheelEnabled;
    this.mouseWheelDelta = Math.max(0, options.mouseWheelDelta);
    this.yOffset = Math.max(0, options.yOffset);
    this.lines = options.lines;
    this.style = options.style;
  }
  /** Create a new viewport with defaults. */
  static new(options = {}) {
    return new _ViewportModel({
      width: options.width ?? 0,
      height: options.height ?? 0,
      keyMap: options.keyMap ?? defaultKeyMap,
      mouseWheelEnabled: options.mouseWheelEnabled ?? true,
      mouseWheelDelta: options.mouseWheelDelta ?? 3,
      yOffset: 0,
      lines: [],
      style: options.style ?? new Style()
    });
  }
  /** Split string content into lines and store it. */
  setContent(content) {
    const normalized = content.replace(/\r\n/g, "\n");
    return this.setContentLines(normalized.split("\n"));
  }
  /** Replace content with a line array. Y offset is clamped if needed. */
  setContentLines(lines) {
    const copy = [...lines];
    const nextOffset = this.clampOffset(this.yOffset, copy, this.height);
    return this.with({ lines: copy, yOffset: nextOffset });
  }
  /** Change width. */
  setWidth(width) {
    if (width === this.width) return this;
    return this.with({ width: Math.max(0, width) });
  }
  /** Change height (and clamp offset if new height is smaller). */
  setHeight(height) {
    const normalized = Math.max(0, height);
    if (normalized === this.height) return this;
    const nextOffset = this.clampOffset(this.yOffset, this.lines, normalized);
    return this.with({ height: normalized, yOffset: nextOffset });
  }
  /** Scroll upward by the given number of lines. */
  scrollUp(lines) {
    if (lines <= 0) return this;
    const nextOffset = Math.max(0, this.yOffset - lines);
    if (nextOffset === this.yOffset) return this;
    return this.with({ yOffset: nextOffset });
  }
  /** Scroll downward by the given number of lines. */
  scrollDown(lines) {
    if (lines <= 0) return this;
    const maxOffset = this.maxYOffset();
    const nextOffset = Math.min(this.yOffset + lines, maxOffset);
    if (nextOffset === this.yOffset) return this;
    return this.with({ yOffset: nextOffset });
  }
  /** Jump to the top. */
  scrollToTop() {
    if (this.atTop()) return this;
    return this.with({ yOffset: 0 });
  }
  /** Jump to the bottom. */
  scrollToBottom() {
    const maxOffset = this.maxYOffset();
    if (this.yOffset === maxOffset) return this;
    return this.with({ yOffset: maxOffset });
  }
  scrollPercent(percent) {
    if (percent === void 0) {
      if (this.height === 0 || this.lines.length === 0) {
        return 1;
      }
      if (this.lines.length <= this.height) {
        return 1;
      }
      const denom = Math.max(1, this.lines.length - this.height);
      return clamp01(this.yOffset / denom);
    }
    const clamped = clamp01(percent);
    const maxOffset = this.maxYOffset();
    const targetOffset = Math.round(maxOffset * clamped);
    if (targetOffset === this.yOffset) return this;
    return this.with({ yOffset: targetOffset });
  }
  /** True when Y offset is at the top. */
  atTop() {
    return this.yOffset <= 0;
  }
  /** True when Y offset is at the bottom. */
  atBottom() {
    return this.yOffset >= this.maxYOffset();
  }
  /** Number of visible lines (without padding). */
  visibleLineCount() {
    return Math.max(0, Math.min(this.height, this.lines.length - this.yOffset));
  }
  /** Total number of content lines. */
  totalLineCount() {
    return this.lines.length;
  }
  /** Move up by n lines (default: 1). */
  lineUp(n = 1) {
    return this.scrollUp(Math.max(1, n));
  }
  /** Move down by n lines (default: 1). */
  lineDown(n = 1) {
    return this.scrollDown(Math.max(1, n));
  }
  /** Move up by half the viewport height. */
  halfViewUp() {
    const delta = Math.max(1, Math.floor(this.height / 2));
    return this.scrollUp(delta);
  }
  /** Move down by half the viewport height. */
  halfViewDown() {
    const delta = Math.max(1, Math.floor(this.height / 2));
    return this.scrollDown(delta);
  }
  /** Move up by one viewport height. */
  viewUp() {
    return this.scrollUp(this.height);
  }
  /** Move down by one viewport height. */
  viewDown() {
    return this.scrollDown(this.height);
  }
  /** Go to a specific line (0-based). Optionally center it. */
  gotoLine(line, centered = false) {
    if (this.lines.length === 0 || this.height === 0) {
      return this;
    }
    const maxIndex = Math.max(0, this.lines.length - 1);
    const targetLine = clamp(Math.floor(line), 0, maxIndex);
    const maxOffset = this.maxYOffset();
    let yOffset = targetLine;
    if (centered) {
      yOffset = targetLine - Math.floor((this.height - 1) / 2);
    }
    const clamped = clamp(yOffset, 0, maxOffset);
    if (clamped === this.yOffset) return this;
    return this.with({ yOffset: clamped });
  }
  /** Tea init hook (no-op). */
  init() {
    return null;
  }
  /** Handle key + mouse scrolling. */
  update(msg) {
    if (msg instanceof MouseMsg) {
      return this.handleMouse(msg);
    }
    if (msg instanceof KeyMsg) {
      return this.handleKey(msg);
    }
    return [this, null];
  }
  /** Render the visible window as a string (padded to height). */
  view() {
    return this.style.render(this.visibleLines().join("\n"));
  }
  /** Build a SyncMsg for high-level renderers. */
  sync() {
    const topLine = this.yOffset;
    const bottomLine = clamp(
      this.yOffset + this.height - 1,
      topLine,
      Math.max(topLine, this.lines.length - 1)
    );
    return () => new SyncMsg(this.visibleLines(), topLine, bottomLine);
  }
  handleKey(msg) {
    if (matches(msg, this.keyMap.down)) {
      const next = this.lineDown();
      return [next, next.scrollCmdIfChanged(this)];
    }
    if (matches(msg, this.keyMap.up)) {
      const next = this.lineUp();
      return [next, next.scrollCmdIfChanged(this)];
    }
    if (matches(msg, this.keyMap.pageDown)) {
      const next = this.viewDown();
      return [next, next.scrollCmdIfChanged(this)];
    }
    if (matches(msg, this.keyMap.pageUp)) {
      const next = this.viewUp();
      return [next, next.scrollCmdIfChanged(this)];
    }
    if (matches(msg, this.keyMap.halfPageDown)) {
      const next = this.halfViewDown();
      return [next, next.scrollCmdIfChanged(this)];
    }
    if (matches(msg, this.keyMap.halfPageUp)) {
      const next = this.halfViewUp();
      return [next, next.scrollCmdIfChanged(this)];
    }
    return [this, null];
  }
  handleMouse(msg) {
    if (!this.mouseWheelEnabled || msg.event.action !== MouseAction.Press || this.height === 0) {
      return [this, null];
    }
    if (msg.event.button === MouseButton.WheelUp) {
      const next = this.scrollUp(this.mouseWheelDelta);
      return [next, next.scrollCmdIfChanged(this)];
    }
    if (msg.event.button === MouseButton.WheelDown) {
      const next = this.scrollDown(this.mouseWheelDelta);
      return [next, next.scrollCmdIfChanged(this)];
    }
    return [this, null];
  }
  visibleLines() {
    const start = this.yOffset;
    const end = Math.min(start + this.height, this.lines.length);
    const window = this.lines.slice(start, end);
    while (window.length < this.height) {
      window.push("");
    }
    return window;
  }
  maxYOffset(lines = this.lines, height = this.height) {
    return Math.max(0, lines.length - height);
  }
  clampOffset(offset, lines, height) {
    return clamp(offset, 0, this.maxYOffset(lines, height));
  }
  with(patch) {
    return new _ViewportModel({
      width: patch.width ?? this.width,
      height: patch.height ?? this.height,
      keyMap: patch.keyMap ?? this.keyMap,
      mouseWheelEnabled: patch.mouseWheelEnabled ?? this.mouseWheelEnabled,
      mouseWheelDelta: patch.mouseWheelDelta ?? this.mouseWheelDelta,
      yOffset: patch.yOffset ?? this.yOffset,
      lines: patch.lines ?? this.lines,
      style: patch.style ?? this.style
    });
  }
  scrollCmdIfChanged(previous) {
    if (this === previous || this.yOffset === previous.yOffset) {
      return null;
    }
    return () => new ScrollMsg(this.scrollPercent(), this.yOffset);
  }
};
function clamp(value, min, max) {
  if (max < min) return min;
  return Math.min(max, Math.max(min, value));
}
function clamp01(value) {
  if (Number.isNaN(value)) return 0;
  return clamp(value, 0, 1);
}

export { ScrollMsg, SyncMsg, ViewportModel, defaultKeyMap };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map